import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_BASE from '../config';
import { useLanguage } from '../LanguageContext';

const AddWorkspaces = () => {
    const { isRtl } = useLanguage();
    const [workspaces, setWorkspaces] = useState([]);
    const [name, setName] = useState('');
    const [loading, setLoading] = useState(false);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [loginData, setLoginData] = useState({ email: '', password: '' });

    const [editingWorkspace, setEditingWorkspace] = useState(null);
    const [editName, setEditName] = useState('');

    const [menuOpenId, setMenuOpenId] = useState(null);
    const [menuPos, setMenuPos] = useState({ x: 0, y: 0 });

    const fetchWorkspaces = async () => {
        try {
            const res = await axios.get(`${API_BASE}/workspaces`);
            setWorkspaces(res.data);
        } catch (err) {
            console.error('Failed to fetch workspaces', err);
        }
    };

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const res = await axios.post(`${API_BASE}/authorized-admin/login`, loginData);
            localStorage.setItem('sys_token', res.data.token);
            axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
            setIsAuthorized(true);
            fetchWorkspaces();
        } catch (err) {
            alert('Invalid System Admin credentials');
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        setLoading(true);
        try {
            await axios.post(`${API_BASE}/workspaces`, { name });
            setName('');
            fetchWorkspaces();
            alert('Workspace created successfully');
        } catch (err) {
            alert(err.response?.data?.msg || 'Failed to create workspace');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (ws) => {
        if (!window.confirm(`Are you absolutely sure you want to delete "${ws.name}"? \n\nCRITICAL: This will PERMANENTLY delete all Products, Raw Materials, Customers, and User accounts associated with this workspace. This action cannot be undone.`)) {
            return;
        }
        setLoading(true);
        try {
            await axios.delete(`${API_BASE}/workspaces/${ws._id}`);
            fetchWorkspaces();
        } catch (err) {
            alert(err.response?.data?.msg || 'Failed to delete workspace');
        } finally {
            setLoading(false);
        }
    };

    const startEdit = (ws) => {
        setEditingWorkspace(ws);
        setEditName(ws.name);
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        if (!editName.trim() || !editingWorkspace) return;
        setLoading(true);
        try {
            await axios.put(`${API_BASE}/workspaces/${editingWorkspace._id}`, { name: editName });
            setEditingWorkspace(null);
            fetchWorkspaces();
        } catch (err) {
            alert(err.response?.data?.msg || 'Failed to update workspace');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const token = localStorage.getItem('sys_token');
        if (token) {
            axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
            setIsAuthorized(true);
            fetchWorkspaces();
        }
        setAuthLoading(false);
    }, []);

    useEffect(() => {
        const onDocClick = (e) => {
            if (!e.target.closest('.floating-menu') && !e.target.closest('.menu-btn')) setMenuOpenId(null);
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, []);

    if (authLoading) return <div style={{ padding: 40, textAlign: 'center' }}>Checking authority...</div>;

    if (!isAuthorized) {
        return (
            <div style={{ maxWidth: 400, margin: '100px auto', padding: 32, background: '#fff', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <h2 style={{ textAlign: 'center', marginBottom: 24 }}>System Admin Login</h2>
                <form onSubmit={handleLogin}>
                    <input
                        type="email" placeholder="Email" required
                        value={loginData.email} onChange={e => setLoginData({ ...loginData, email: e.target.value })}
                        style={{ width: '100%', marginBottom: 12, padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <input
                        type="password" placeholder="Password" required
                        value={loginData.password} onChange={e => setLoginData({ ...loginData, password: e.target.value })}
                        style={{ width: '100%', marginBottom: 20, padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                    />
                    <button type="submit" disabled={loading} style={{ width: '100%', padding: 12, background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>
                        {loading ? 'Logging in...' : 'Login to System'}
                    </button>
                </form>
            </div>
        );
    }

    return (
        <div style={{ padding: 40, maxWidth: 1100, margin: '0 auto' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
                <h1>Workspace Management</h1>
                <button onClick={() => { localStorage.removeItem('sys_token'); setIsAuthorized(false); }} style={{ color: '#d72323', cursor: 'pointer', background: 'none', border: 'none', fontWeight: 500 }}>Logout System Admin</button>
            </div>

            {editingWorkspace ? (
                <div style={{ background: '#fff', padding: 24, borderRadius: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: 32, borderLeft: '4px solid #1976d2' }}>
                    <h3 style={{ marginBottom: 16 }}>Edit Workspace Name</h3>
                    <form onSubmit={handleUpdate} style={{ display: 'flex', gap: 12 }}>
                        <input
                            type="text" required
                            value={editName} onChange={e => setEditName(e.target.value)}
                            style={{ flex: 1, padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                        <button type="submit" disabled={loading} style={{ padding: '0 24px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>
                            {loading ? 'Updating...' : 'Save Changes'}
                        </button>
                        <button type="button" onClick={() => setEditingWorkspace(null)} style={{ padding: '0 24px', background: '#f1f2f4', color: '#6d7175', border: 'none', borderRadius: 6, fontWeight: 600 }}>
                            Cancel
                        </button>
                    </form>
                </div>
            ) : (
                <div style={{ background: '#fff', padding: 24, borderRadius: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', marginBottom: 32 }}>
                    <h3 style={{ marginBottom: 16 }}>Register New Client Organization</h3>
                    <form onSubmit={handleAdd} style={{ display: 'flex', gap: 12 }}>
                        <input
                            type="text" placeholder="Workspace Name (e.g. Acme Corp)" required
                            value={name} onChange={e => setName(e.target.value)}
                            style={{ flex: 1, padding: 12, borderRadius: 6, border: '1px solid #ccc' }}
                        />
                        <button type="submit" disabled={loading} style={{ padding: '0 24px', background: '#1976d2', color: '#fff', border: 'none', borderRadius: 6, fontWeight: 600 }}>
                            {loading ? 'Creating...' : 'Create Workspace'}
                        </button>
                    </form>
                </div>
            )}

            <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 2px 4px rgba(0,0,0,0.05)', overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead style={{ background: '#f6f6f7' }}>
                        <tr>
                            <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '16px 24px', fontSize: 13, color: '#6d7175' }}>Organization Name</th>
                            <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '16px 24px', fontSize: 13, color: '#6d7175' }}>UUID</th>
                            <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '16px 24px', fontSize: 13, color: '#6d7175' }}>Admin Email</th>
                            <th style={{ textAlign: isRtl ? 'right' : 'left', padding: '16px 24px', fontSize: 13, color: '#6d7175' }}>Admin Password</th>
                            <th style={{ textAlign: isRtl ? 'left' : 'right', padding: '16px 24px', fontSize: 13, color: '#6d7175' }}>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {workspaces.map(ws => (
                            <tr key={ws._id} style={{ borderTop: '1px solid #eef0f2' }}>
                                <td style={{ padding: '16px 24px', fontWeight: 600 }}>{ws.name}</td>
                                <td style={{ padding: '16px 24px', fontSize: 12, fontFamily: 'monospace' }}>{ws.workspaceUuid}</td>
                                <td style={{ padding: '16px 24px' }}>{ws.generatedAdminEmail}</td>
                                <td style={{ padding: '16px 24px' }}>
                                    <span style={{ background: '#f1f2f4', padding: '4px 8px', borderRadius: 4, fontFamily: 'monospace' }}>{ws.generatedAdminPassword}</span>
                                </td>
                                <td style={{ padding: '16px 24px', textAlign: isRtl ? 'left' : 'right' }}>
                                    <button
                                        className="menu-btn"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            setMenuPos({ x: isRtl ? rect.right : rect.left, y: rect.bottom + 8 });
                                            setMenuOpenId(menuOpenId === ws._id ? null : ws._id);
                                        }}
                                        style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#6d7175', padding: 8, borderRadius: 4 }}
                                    >
                                        <svg width="20" height="20" viewBox="0 0 20 20"><path d="M6 10a2 2 0 11-4 0 2 2 0 014 0zM12 10a2 2 0 11-4 0 2 2 0 014 0zM18 10a2 2 0 11-4 0 2 2 0 014 0z" fill="currentColor" /></svg>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {menuOpenId && (
                <div className="floating-menu" style={{
                    position: 'fixed',
                    [isRtl ? 'right' : 'left']: isRtl ? (window.innerWidth - menuPos.x) : (menuPos.x - 120),
                    top: menuPos.y, background: '#fff',
                    borderRadius: 8, boxShadow: '0 12px 30px rgba(0,0,0,0.15)', minWidth: 160, zIndex: 2000,
                    border: '1px solid #ebebeb'
                }} onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', flexDirection: 'column', padding: 4 }}>
                        <button onClick={() => { const ws = workspaces.find(w => w._id === menuOpenId); setMenuOpenId(null); if (ws) startEdit(ws); }} style={{
                            padding: '8px 12px', textAlign: 'left', border: 'none', background: 'transparent',
                            cursor: 'pointer', color: '#1a1c1d', fontSize: 13, borderRadius: 4
                        }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>Edit</button>
                        <button onClick={() => { const ws = workspaces.find(w => w._id === menuOpenId); setMenuOpenId(null); if (ws) handleDelete(ws); }} style={{
                            padding: '8px 12px', textAlign: 'left', border: 'none', background: 'transparent',
                            cursor: 'pointer', color: '#d82c0d', fontSize: 13, borderRadius: 4
                        }} onMouseOver={e => e.target.style.background = '#f1f1f1'} onMouseOut={e => e.target.style.background = 'transparent'}>Delete</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AddWorkspaces;
